import type { ReactNode } from 'react'
import cx from 'classnames'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { Form } from '@/components/Form'
import { Button } from '@/components/Button'

import { TabBar, TabItem } from '@/components/TabBar'
import {
  InfoGatheringDialogFieldGrid,
  InfoGatheringDialogScrollArea,
  InfoGatheringDialogSubsectionHeader,
} from '@/features/jobs/info-gathering/components/InfoGatheringDialog'
import styles from './EditPersonalProfileDialog.module.css'
import type { PersonalProfileTab } from './EditPersonalProfileDialogViewModel'

export interface EditPersonalProfileDialogTab {
  id: PersonalProfileTab
  label: string
}

export interface EditPersonalProfileDialogProps {
  showing: boolean
  onRequestClose?: () => unknown
  tabs: EditPersonalProfileDialogTab[]
  activeTab: PersonalProfileTab
  activeTabLabel: string
  onTabChange: (tab: PersonalProfileTab) => unknown
  showBack: boolean
  onBack: () => unknown
  showSkip: boolean
  onSkip: () => unknown
  submitLabel: string
  onSubmit: () => unknown
  isSubmitPending: boolean
  children?: ReactNode
}

export function EditPersonalProfileDialog({
  showing,
  onRequestClose,
  tabs,
  activeTab,
  activeTabLabel,
  onTabChange,
  showBack,
  onBack,
  showSkip,
  onSkip,
  submitLabel,
  onSubmit,
  isSubmitPending,
  children,
}: EditPersonalProfileDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={onRequestClose}
      disableCloseOnOutsideClick
      disableCloseOnEsc
    >
      <ModalSheetHeader withCloseButton>
        <ModalSheetTitle emphasized>Fill in personal info</ModalSheetTitle>
      </ModalSheetHeader>
      <TabBar bordered>
        {tabs.map((tab) => (
          <TabItem
            key={tab.id}
            active={activeTab === tab.id}
            onPress={() => onTabChange(tab.id)}
            testId={`edit-personal-tab-${tab.id}`}
          >
            {tab.label}
          </TabItem>
        ))}
      </TabBar>
      <ModalSheetContent>
        <ModalSheetSection spaced>
          <InfoGatheringDialogScrollArea>
            <Form>{children}</Form>
          </InfoGatheringDialogScrollArea>
        </ModalSheetSection>
      </ModalSheetContent>
      <ModalSheetFooter>
        {showBack && (
          <ModalSheetFooterButtonGroup>
            <Button
              label="Back"
              variant="secondary"
              onPress={onBack}
              isDisabled={isSubmitPending}
              testId="edit-personal-back-button"
            />
          </ModalSheetFooterButtonGroup>
        )}
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          {showSkip && (
            <Button
              label={`Skip ${activeTabLabel}`}
              variant="secondary"
              onPress={onSkip}
              isDisabled={isSubmitPending}
              testId="edit-personal-skip-button"
            />
          )}
          <Button
            label={submitLabel}
            variant="primary"
            onPress={onSubmit}
            isPending={isSubmitPending}
            isDisabled={isSubmitPending}
            testId="edit-personal-submit-button"
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export interface EditPersonalProfileDialogSectionProps {
  title: string
  bordered?: boolean
  children?: ReactNode
}

export function EditPersonalProfileDialogSection({
  title,
  bordered,
  children,
}: EditPersonalProfileDialogSectionProps) {
  return (
    <>
      <InfoGatheringDialogSubsectionHeader bordered={bordered}>
        {title}
      </InfoGatheringDialogSubsectionHeader>
      <InfoGatheringDialogFieldGrid>{children}</InfoGatheringDialogFieldGrid>
    </>
  )
}

export interface EditPersonalProfileDependentsEmptyStateProps {
  onAddDependent: () => unknown
  isAddDisabled: boolean
}

export function EditPersonalProfileDependentsEmptyState({
  onAddDependent,
  isAddDisabled,
}: EditPersonalProfileDependentsEmptyStateProps) {
  return (
    <div className={styles.dependentsEmptyState}>
      <div className={styles.dependentsEmptyStatePrompt}>
        Does the client have any dependents to claim?
      </div>
      <Button
        label="+ Add New Dependent"
        variant="secondary"
        onPress={onAddDependent}
        isDisabled={isAddDisabled}
        testId="edit-personal-add-dependent-button"
      />
    </div>
  )
}

export interface EditPersonalProfileDependentRowProps {
  index: number
  onRemove: () => unknown
  isRemoveDisabled: boolean
  children?: ReactNode
}

export function EditPersonalProfileDependentRow({
  index,
  onRemove,
  isRemoveDisabled,
  children,
}: EditPersonalProfileDependentRowProps) {
  return (
    <div
      className={cx(
        styles.dependentRow,
        index > 0 && styles.dependentRowBordered,
      )}
    >
      <div className={styles.dependentRowHeader}>
        <div className={styles.dependentRowTitle}>Dependent #{index + 1}</div>
        <Button
          label="Remove"
          variant="text"
          iconLeft="delete"
          iconSize={24}
          className={styles.dependentRowRemoveButton}
          onPress={onRemove}
          isDisabled={isRemoveDisabled}
          testId={`edit-personal-remove-dependent-${index}`}
        />
      </div>
      <InfoGatheringDialogFieldGrid>{children}</InfoGatheringDialogFieldGrid>
    </div>
  )
}

export interface EditPersonalProfileAddDependentRowProps {
  onAddDependent: () => unknown
  isAddDisabled: boolean
}

export function EditPersonalProfileAddDependentRow({
  onAddDependent,
  isAddDisabled,
}: EditPersonalProfileAddDependentRowProps) {
  return (
    <div className={styles.addDependentRow}>
      <Button
        label="+ Add New Dependent"
        variant="secondary"
        onPress={onAddDependent}
        isDisabled={isAddDisabled}
        testId="edit-personal-add-dependent-button"
      />
    </div>
  )
}
